import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, CreditCard, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_PLANS = {
  single: {
    type: 'single_report',
    name: 'Single Report',
    eurPrice: 50,
    mkdPrice: 3000
  },
  basic: {
    type: 'subscription',
    plan: 'basic',
    name: 'Basic Plan',
    eurPrice: 1000,
    mkdPrice: 62000,
    reports: 1000
  },
  professional: {
    type: 'subscription',
    plan: 'professional',
    name: 'Professional Plan',
    eurPrice: 2500,
    mkdPrice: 154000,
    reports: 3000
  },
  enterprise: {
    type: 'subscription',
    plan: 'enterprise',
    name: 'Enterprise Plan',
    eurPrice: 4000,
    mkdPrice: 246000,
    reports: 5000
  }
};

const PaymentModal = ({ assessmentId, executionId, paymentType, onClose }) => {
  const [currency, setCurrency] = useState('eur');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const plan = PAYMENT_PLANS[paymentType];
  const price = currency === 'eur' ? plan.eurPrice : plan.mkdPrice;
  const currencySymbol = currency === 'eur' ? '€' : 'MKD';

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError('');

      const originUrl = window.location.origin;

      const paymentRequest = {
        assessment_id: assessmentId,
        execution_id: executionId,
        payment_type: plan.type,
        currency: currency,
        origin_url: originUrl
      };

      if (plan.type === 'subscription') {
        paymentRequest.subscription_plan = plan.plan;
      }

      const response = await axios.post(`${API}/payments/checkout`, paymentRequest);

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Failed to process payment. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="payment-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="close-payment-modal"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <CreditCard className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
          {plan.reports && (
            <p className="text-gray-600">{plan.reports.toLocaleString()} reports per month</p>
          )}
        </div>

        {/* Currency Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Currency</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setCurrency('eur')}
              className={`p-4 border-2 rounded-lg transition-all ${
                currency === 'eur'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
              data-testid="currency-eur"
            >
              <div className="text-2xl font-bold">€{plan.eurPrice}</div>
              <div className="text-sm">EUR</div>
            </button>
            <button
              onClick={() => setCurrency('mkd')}
              className={`p-4 border-2 rounded-lg transition-all ${
                currency === 'mkd'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
              data-testid="currency-mkd"
            >
              <div className="text-2xl font-bold">{plan.mkdPrice}</div>
              <div className="text-sm">MKD</div>
            </button>
          </div>
        </div>

        {/* Payment Details */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Amount:</span>
            <span className="font-bold text-gray-900">{currencySymbol}{price}</span>
          </div>
          {plan.type === 'subscription' && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Billing:</span>
              <span className="text-gray-900">Monthly</span>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Payment Info */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-gray-700">
          <p className="mb-2">
            <strong>Payment Methods:</strong> VISA, MasterCard
          </p>
          {plan.type === 'subscription' && (
            <p>
              <strong>Invoice Option:</strong> Contact us for invoice billing
            </p>
          )}
        </div>

        {/* Payment Button */}
        <button
          onClick={handlePayment}
          disabled={loading}
          className="w-full bg-gradient-to-r from-emerald-600 to-blue-600 text-white py-4 rounded-lg font-bold text-lg hover:from-emerald-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="proceed-to-payment-btn"
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <Loader className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </span>
          ) : (
            `Pay ${currencySymbol}${price}`
          )}
        </button>

        <p className="text-xs text-gray-500 text-center mt-4">
          Secure payment powered by Stripe
        </p>
      </div>
    </div>
  );
};

export default PaymentModal;
